import { cellCenter } from '../grid.ts';
import { imax, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Hero, SendaNpc, TornoRules, TornoState } from '../types.ts';
import { damageHero, ev } from './combat.ts';
import { orderMove } from './heroes.ts';
import { pulseLand } from './brasa.ts';
import { gargFlock, gargOn, gargTick } from './garg.ts';
import { shootTick } from './workshop.ts';

/**
 * The winch (docs/sendas/artifice.md, chapter 5, act 2; the lesson of chapter 2: a ratio is two stages multiplied).
 * Tormo climbs Grada's wall to the Raja, the biggest thing there is to break; Dunna hooked the cable's cable to his
 * harness, and the winch on the ledge can pull him off. It takes strength: the slowest the box gives.
 *
 * The box: A (the motor, `input`), B with its two places (B1 meshes with A, B2 with C) and C (the drum). The drum turns
 * at `input`·A/B1·B2/C; it pulls the inverse. Tormo's mark asks the slowest the shelf allows (`wantNum/wantDen`): both
 * stages slowing, a small gear driving a big one twice. The obvious (the biggest at the drum) is one stage: not enough.
 * Taps on a slot or a shelf spot send the hero; standing still by it `stand` swaps what it carries with what is there
 * (a gear in its hands slows it, `carryBp` by its teeth). The lever (`pull`) pulls: the box not full, nothing; too fast,
 * the cable slips (Tormo climbs a span, the cable takes `slipBlows`); right, it hauls him `haul` and he lets go. A gear
 * taken out while it hauls stops it (what was hauled stays). Not held, he climbs a span every `climb`; he starts `spans`
 * from the Raja and reaching it, opens it: lost.
 *
 * The gargoyles (battle/garg.ts) come off the abyss in flocks and sit on the cable: each one perched takes a blow off it
 * every `peck`; at `cable` blows it is cut. The rest go for the hero or Dunna. The Raja sheds stone on the ledge
 * (`rocks`, warned 3 s: one on the hero, one on the box). Dunna by the winch with her bolts.
 *
 * Cost: the box's speed worked out at a pull; the flyers' (garg.ts).
 */

const fresh = (r: TornoRules): TornoState => ({
  on: r.pieces.map((_, k) => 4 + k),
  hand: -1,
  job: -1,
  held: 0,
  haul: -1,
  hauled: 0,
  spans: r.spans,
  cable: r.cable,
  climb: r.climb,
  slips: 0,
  last: 0,
  lastAt: -1,
  flock: r.flock.first,
  peck: r.peck,
  rocks: r.rocks.first,
  spots: [],
  shower: r.shower.first,
  showerAt: -1,
  shot: 0,
  frayed: false,
  end: -1,
  lost: -1,
});

/** A mark's cell: a slot (0–3), a shelf spot (4…), the lever (4 + the gears). */
export const tornoCell = (r: TornoRules, at: number): number => (at < 4 ? (r.slots[at] as number) : at < 4 + r.pieces.length ? (r.lie[at - 4] as number) : r.lever);

/** The gear in a slot or spot (−1 none). */
export const tornoAt = (w: TornoState, slot: number): number => w.on.indexOf(slot);

/** The teeth in A, B1, B2, C as they are (0: empty). */
export const tornoBox = (r: TornoRules, w: TornoState): number[] =>
  [0, 1, 2, 3].map((k) => {
    const g = tornoAt(w, k);
    return g >= 0 ? (r.pieces[g] as number) : 0;
  });

/** The drum's speed as the box is (num, den; den 0 while it isn't full). */
export const tornoSpeed = (r: TornoRules, w: TornoState): { num: number; den: number } => {
  const [a, b1, b2, c] = tornoBox(r, w) as [number, number, number, number];
  if (!a || !b1 || !b2 || !c) return { num: 0, den: 0 };
  return { num: r.input * a * b2, den: b1 * c };
};

/** Whether the box pulls hard enough (its speed no more than the mark's). */
export const tornoStrong = (r: TornoRules, w: TornoState): boolean => {
  const v = tornoSpeed(r, w);
  return v.den > 0 && v.num * r.wantDen <= r.wantNum * v.den;
};

/** The hero's pace with a gear in its hands. */
export const tornoCarryBp = (s: BattleState): number => {
  const r = s.rules?.senda?.torno;
  const w = s.senda?.torno;
  if (!r || !w || w.hand < 0) return 10000;
  const t = r.pieces[w.hand] as number;
  return r.carryBp[t >= 36 ? 2 : t >= 24 ? 1 : 0] as number;
};

export const tornoOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.torno;
  const w = s.senda?.torno;
  if (!r || !w || w.end >= 0 || at < 0 || at > 4 + r.pieces.length) return;
  w.job = at;
  w.held = 0;
  const c = cellCenter(s.map.radius, tornoCell(r, at));
  orderMove(s, h, c.x, c.y);
  h.target = 0;
  h.forced = false;
};

const lose = (s: BattleState, w: TornoState, why: string, cue: (c: string) => void): void => {
  if (w.lost >= 0) return;
  w.lost = s.tick;
  ev(s, { t: 'senda', kind: 'tornoLost', npc: why, x: 0, y: 0 });
  cue(why);
};

const pull = (s: BattleState, r: TornoRules, w: TornoState, cue: (c: string) => void): void => {
  const c = cellCenter(s.map.radius, r.lever);
  w.lastAt = s.tick;
  if (tornoSpeed(r, w).den === 0) {
    w.last = 3;
    ev(s, { t: 'senda', kind: 'pullLoose', npc: '', x: c.x, y: c.y });
    return;
  }
  if (tornoStrong(r, w)) {
    w.last = 1;
    if (w.haul < 0) w.haul = s.tick;
    ev(s, { t: 'senda', kind: 'haul', npc: '', x: r.drum.x, y: r.drum.y });
    cue('haul');
    return;
  }
  w.last = 2;
  w.slips++;
  w.spans--;
  w.cable = imax(0, w.cable - r.slipBlows);
  w.climb = s.waveT + r.climb;
  ev(s, { t: 'senda', kind: 'slip', npc: '', x: r.drum.x, y: r.drum.y, n: w.spans });
  cue('slip');
};

const heroWork = (s: BattleState, r: TornoRules, w: TornoState, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job < 0) return;
  const c = cellCenter(s.map.radius, tornoCell(r, w.job));
  if (h.moving || !withinRange(h.x, h.y, c.x, c.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  const lever = w.job === 4 + r.pieces.length;
  if (++w.held < (lever ? r.pull : r.stand)) return;
  const at = w.job;
  w.job = -1;
  w.held = 0;
  if (lever) {
    pull(s, r, w, cue);
    return;
  }
  const there = tornoAt(w, at);
  if (there < 0 && w.hand < 0) return;
  if (there >= 0) {
    w.on[there] = -1;
    ev(s, { t: 'senda', kind: 'take', npc: '', x: c.x, y: c.y, n: at });
    // A gear out of the box while it hauls: it stops (what was hauled stays).
    if (at < 4 && w.haul >= 0) {
      w.haul = -1;
      ev(s, { t: 'senda', kind: 'haulStop', npc: '', x: r.drum.x, y: r.drum.y });
    }
  }
  if (w.hand >= 0) {
    w.on[w.hand] = at;
    ev(s, { t: 'senda', kind: 'put', npc: '', x: c.x, y: c.y, n: at });
  }
  w.hand = there;
};

/** Stone off the Raja: one on the hero, one on the box. */
const rocks = (s: BattleState, r: TornoRules, w: TornoState): void => {
  if (s.waveT < w.rocks) return;
  w.rocks = s.waveT + r.rocks.every;
  const t = s.tick + r.rocks.warn;
  const add = (x: number, y: number): void => {
    w.spots.push({ x, y, t });
    ev(s, { t: 'senda', kind: 'pulseWarn', npc: '', x, y, n: r.rocks.warn });
  };
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h) add(h.x + rngRange(s.rng, -300, 300), h.y + rngRange(s.rng, -300, 300));
  const c = cellCenter(s.map.radius, r.slots[rngInt(s.rng, 4)] as number);
  if (!h || !withinRange(h.x, h.y, c.x, c.y, r.rocks.r * 2)) add(c.x, c.y);
};

/** A shower off the Raja over the whole ledge: warned, then a share of the hero's life wherever it stands. */
const shower = (s: BattleState, r: TornoRules, w: TornoState): void => {
  const S = r.shower;
  if (w.showerAt < 0 && s.waveT >= w.shower) {
    w.showerAt = s.tick + S.warn;
    ev(s, { t: 'senda', kind: 'showerWarn', npc: '', x: r.top.x, y: r.top.y, n: S.warn });
    return;
  }
  if (w.showerAt < 0 || s.tick < w.showerAt) return;
  w.showerAt = -1;
  w.shower = s.waveT + (w.haul >= 0 ? S.haul : S.every) - S.warn;
  ev(s, { t: 'senda', kind: 'shower', npc: '', x: r.drum.x, y: r.drum.y });
  for (const h of s.heroes) if (h.downTicks === 0) damageHero(s, h, mulBp(h.stats.maxHp, S.heroBp), null, 'physical', true);
};

export const tornoTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = s.rules?.senda?.torno;
  const st = s.senda;
  if (!r || !st) return;
  const w = (st.torno ??= fresh(r));
  if (!s.started || w.end >= 0 || w.lost >= 0) return;
  gargTick(s);
  heroWork(s, r, w, cue);
  if (w.end >= 0) return;
  if (w.haul >= 0) {
    if (++w.hauled >= r.haul) {
      w.end = s.tick;
      w.haul = -1;
      ev(s, { t: 'senda', kind: 'tornoFree', npc: '', x: r.top.x, y: r.top.y });
      cue('free');
      return;
    }
  } else if (s.waveT >= w.climb) {
    w.climb = s.waveT + r.climb;
    w.spans--;
    ev(s, { t: 'senda', kind: 'climb', npc: '', x: r.top.x, y: r.top.y, n: w.spans });
    cue('climb');
  }
  if (w.spans <= 0) {
    lose(s, w, 'raja', cue);
    return;
  }
  if (s.waveT >= w.flock) {
    w.flock = s.waveT + r.flock.every;
    gargFlock(s, r.flock.n, 0, [2], r.flock.perchBp);
  }
  if (s.waveT >= w.peck) {
    w.peck = s.waveT + r.peck;
    const n = gargOn(s, 2);
    if (n > 0) {
      w.cable = imax(0, w.cable - n);
      ev(s, { t: 'senda', kind: 'fray', npc: '', x: r.drum.x, y: r.drum.y, n: w.cable });
      cue('perch');
    }
  }
  if (!w.frayed && w.cable * 2 <= r.cable) {
    w.frayed = true;
    cue('cable');
  }
  if (w.cable <= 0) {
    lose(s, w, 'cut', cue);
    return;
  }
  rocks(s, r, w);
  shower(s, r, w);
  pulseLand(s, w.spots, r.rocks);
  const d = st.npcs.find((n): n is SendaNpc => n.id === 'dunna' && n.hp > 0);
  if (d) {
    if (d.flinch > 0) d.flinch--;
    w.shot = shootTick(s, d, r.dunna, w.shot);
  }
};

/** Three spans of haul (the cable's progress): what was hauled of the whole. */
export const tornoGoal = (s: BattleState): { done: number; need: number } => {
  const r = s.rules?.senda?.torno;
  const w = s.senda?.torno;
  if (!r || !w) return { done: 0, need: 1 };
  return { done: w.end >= 0 ? r.haul : w.hauled, need: r.haul };
};

export const tornoResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = s.senda?.torno;
  if (!w) return null;
  if (w.lost >= 0) return 'lost';
  return w.end >= 0 && s.tick >= w.end + 24 ? 'won' : null;
};
